/**
 * MUI dark theme for the v2 stats stack.
 *
 * The app at large does NOT use a MUI ThemeProvider — it relies on the
 * default light palette. v2 dialogs and form controls (TextField, Select,
 * MenuItem, Button…) need a dark palette so labels and input text stay
 * readable on the dark surfaces from useStatsTheme().
 *
 * StatsRouter wraps its render tree in <ThemeProvider theme={muiV2Theme}>
 * so every MUI component inside (including portaled Dialogs) inherits this
 * palette via React context.
 *
 * The accent colour comes from ClubThemeContext via the buildMuiV2Theme
 * factory, so the orange/blue/whatever the club uses is consistent end-to-end.
 */

import { createTheme } from '@mui/material/styles';
import {
  STATS_SURFACES,
  STATS_TEXT,
  STATS_ACTION_COLORS,
  STATS_RADIUS,
  STATS_TYPOGRAPHY,
} from './statsTheme';

export function buildMuiV2Theme({ accent, accentSecondary }) {
  return createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: accent || '#F25F29',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: accentSecondary || '#FF7043',
        contrastText: '#FFFFFF',
      },
      success: { main: STATS_ACTION_COLORS.made },
      error:   { main: STATS_ACTION_COLORS.missed },
      warning: { main: STATS_ACTION_COLORS.warning },
      info:    { main: STATS_ACTION_COLORS.info },
      background: {
        default: STATS_SURFACES.base,
        paper:   STATS_SURFACES.elevated,
      },
      text: {
        primary:   STATS_TEXT.primary,
        secondary: STATS_TEXT.secondary,
        disabled:  STATS_TEXT.muted,
      },
      divider: STATS_SURFACES.divider,
      action: {
        active:        STATS_TEXT.primary,
        hover:         'rgba(255,255,255,0.08)',
        selected:      'rgba(255,255,255,0.12)',
        disabled:      STATS_TEXT.muted,
        disabledBackground: 'rgba(255,255,255,0.06)',
      },
    },
    typography: {
      fontFamily: STATS_TYPOGRAPHY.fontFamily,
    },
    shape: {
      // CRITICAL: keep this at 1 (the identity multiplier).
      //
      // MUI's `sx={{ borderRadius: N }}` multiplies N by `shape.borderRadius`
      // to produce the final pixel value. Our v2 components pass raw pixel
      // numbers via `theme.radius.xxx` (e.g. 20 for xl), and they expect
      // those numbers to land as-is. If shape.borderRadius is e.g. 10, a
      // dialog with `borderRadius: 20` ends up at 200px → giant ovals.
      //
      // With shape.borderRadius=1, the multiplication is identity and
      // every `theme.radius.xxx` value becomes its literal pixel count.
      borderRadius: 1,
    },
    components: {
      // TextField — make label, input text, and borders all readable
      MuiTextField: {
        defaultProps: { variant: 'outlined' },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: STATS_TEXT.primary,
            backgroundColor: STATS_SURFACES.floating,
            // Keep input radius small (6px) so the floating-label notch
            // does not collide with the rounded corner. Cards / dialogs
            // keep their bigger radius via their own sx.
            borderRadius: 6,
          },
          input: {
            color: STATS_TEXT.primary,
            '&::placeholder': {
              color: STATS_TEXT.muted,
              opacity: 1,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: STATS_SURFACES.floating,
            borderRadius: 6,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: STATS_SURFACES.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255,255,255,0.24)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
          },
          input: {
            color: STATS_TEXT.primary,
          },
          // Give the label legend enough horizontal padding so the text
          // never gets clipped by the rounded corners.
          notchedOutline: {
            '& legend': {
              marginLeft: 4,
              paddingLeft: 6,
              paddingRight: 6,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: STATS_TEXT.secondary,
            '&.Mui-focused': {
              color: 'inherit',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: STATS_TEXT.secondary,
          },
        },
      },
      // Select dropdown menu
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: STATS_SURFACES.elevated,
            color: STATS_TEXT.primary,
            border: `1px solid ${STATS_SURFACES.border}`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: STATS_TEXT.primary,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
            '&.Mui-selected': {
              backgroundColor: 'rgba(255,255,255,0.12)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: STATS_TEXT.secondary,
          },
        },
      },
      // Buttons
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: STATS_RADIUS.md,
          },
          text: {
            color: STATS_TEXT.primary,
          },
        },
      },
      // Dialog default surfaces (already overridden inline in our dialogs but
      // this ensures consistency for any dialog we forget)
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: STATS_SURFACES.elevated,
            color: STATS_TEXT.primary,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: STATS_TEXT.primary,
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            color: STATS_TEXT.secondary,
          },
        },
      },
      // Tooltip on dark background
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: 'rgba(20,24,32,0.95)',
            color: STATS_TEXT.primary,
            border: `1px solid ${STATS_SURFACES.border}`,
            fontSize: 11,
            fontWeight: 600,
          },
          arrow: {
            color: 'rgba(20,24,32,0.95)',
          },
        },
      },
      // Tabs
      MuiTab: {
        styleOverrides: {
          root: {
            color: STATS_TEXT.secondary,
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-selected': {
              color: STATS_TEXT.primary,
            },
          },
        },
      },
    },
  });
}
